/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable max-len */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import defaultListItemComponents from './defaultDraggableListItemComponents';

import styles from '../styles/draggableListItem.module.scss';

const DraggableListItem = ({
  item,
  className = '',
  classNameActions = '',
  onExpand,
  onCollapse,
  provided,
  components = defaultListItemComponents,
  kebabMenuItems = [],
  actionMenuItems = [],
  onKebabMenuItemClick = () => null,
  onActionItemClick = () => null,
  onContentActionClick = () => null,
  snapshot,
  isKebabDisabled = false,
  isAddButtonDisabled = false,
  isDragDisabled = false,
  dragTooltipText,
  addButtonTooltipText,
  kebabTooltipText,
  testId,
  ...rest
}) => {
  const { Actions, Content, DragHandle, KebabMenu, ToggleExpandButton } = {
    ...defaultListItemComponents,
    ...components,
  };

  const { isDragging } = snapshot;

  const [isKebabMenuOpen, setIsKebabMenuOpen] = useState(false);

  const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);

  const onKebabMenuIsOpenChange = ({ isOpen }) => setIsKebabMenuOpen(isOpen);

  const onActionMenuIsOpenChange = ({ isOpen }) => setIsActionMenuOpen(isOpen);

  return (
    <div
      ref={provided.innerRef}
      {...provided.draggableProps}
      id={`draggable-${item.id}`}
      data-testid={testId || `draggable-${item.id}`}
      className={classnames(styles.element, className, {
        [styles.isShowingDragShadow]: isDragging,
        [styles.isPersistent]: isKebabMenuOpen || isActionMenuOpen,
      })}
    >
      <div className={styles.content}>
        <div>
          <div className={classnames(styles.actions, classNameActions)}>
            <Actions
              item={item}
              onActionItemClick={onActionItemClick}
              onIsOpenChange={onActionMenuIsOpenChange}
              actionMenuItems={actionMenuItems}
              isAddButtonDisabled={isAddButtonDisabled}
              addButtonTooltipText={addButtonTooltipText}
            />
            <DragHandle
              item={item}
              provided={provided}
              isDragDisabled={isDragDisabled}
              dragTooltipText={dragTooltipText}
            />
          </div>
          <ToggleExpandButton item={item} onExpand={onExpand} onCollapse={onCollapse} />
          <Content item={item} onContentActionClick={onContentActionClick} onContentActiveClassName={styles.contentActiveComponent} {...rest} />
        </div>
        <div className={styles.context}>
          <div className={styles.kebab}>
            <KebabMenu
              item={item}
              isKebabDisabled={isKebabDisabled}
              kebabMenuItems={kebabMenuItems}
              onKebabMenuItemClick={onKebabMenuItemClick}
              onIsOpenChange={onKebabMenuIsOpenChange}
              kebabTooltipText={kebabTooltipText}
            />
          </div>
        </div>
      </div>
      {provided.placeholder}
    </div>
  );
};

DraggableListItem.propTypes = {
  isKebabDisabled: PropTypes.bool,
  isAddButtonDisabled: PropTypes.bool,
  isDragDisabled: PropTypes.bool,
  iconName: PropTypes.string,
  className: PropTypes.string,
  classNameActions: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string]),
  components: PropTypes.shape({}),
  provided: PropTypes.shape({
    innerRef: PropTypes.func,
    dragHandleProps: PropTypes.shape({}),
    draggableProps: PropTypes.shape({}),
    placeholder: PropTypes.shape({}),
  }).isRequired,
  kebabMenuItems: PropTypes.arrayOf(PropTypes.shape({})),
  actionMenuItems: PropTypes.arrayOf(PropTypes.shape({})),
  onKebabMenuItemClick: PropTypes.func,
  onActionItemClick: PropTypes.func,
  onContentActionClick: PropTypes.func,
  onExpand: PropTypes.func.isRequired,
  onCollapse: PropTypes.func.isRequired,
  item: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }).isRequired,
  snapshot: PropTypes.shape({
    isDragging: PropTypes.bool,
  }).isRequired,
};

export default React.memo(DraggableListItem);
